import React, { useEffect, useState, useCallback } from 'react';
import { Joyride, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/App';
import { track } from '@/lib/analytics';

/**
 * PLANIVANCE AI Guided Tour — react-joyride wrapped in the platform's
 * navy/gold visual system. Steps navigate the user through 11 core modules.
 * Concise 1-3 sentence prompts. Analytics for started / completed / skipped /
 * restarted routed to /api/beta-program/tour-status.
 */

const TOUR_STEPS = [
  {
    route: '/',
    target: '[data-testid="dashboard-page"]',
    title: 'Executive Dashboard',
    content: "Your live snapshot of income, expenses, savings rate, and retirement readiness — the first place to check whether you're on track.",
  },
  {
    route: '/income-sources',
    target: '[data-testid="income-sources-page"]',
    title: 'Income Sources',
    content: 'Enter every income stream — W-2, self-employment, Social Security, pension, or portfolio. This feeds every downstream retirement calculation.',
  },
  {
    route: '/spending-tracker',
    target: '[data-testid="spending-tracker-page"]',
    title: 'Expenses',
    content: 'Log spending by category to understand your monthly and annual outflow. Needs vs. wants tagging helps surface the easiest wins.',
  },
  {
    route: '/net-worth',
    target: '[data-testid="net-worth-page"]',
    title: 'Net Worth',
    content: 'Assets minus liabilities gives you the complete financial picture. It updates every KPI on your dashboard.',
  },
  {
    route: '/wealth-health',
    target: '[data-testid="wealth-health-page"]',
    title: 'Wealth Health Score™',
    content: 'A 0–100 composite showing your financial strengths across five dimensions. This is a self-assessment tool — not a credit score, investment recommendation, or guarantee of outcomes.',
  },
  {
    route: '/retirement-gap',
    target: '[data-testid="retirement-gap-page"]',
    title: 'Retirement Gap Analyzer',
    content: 'Compare projected retirement income vs. your retirement goals across your expected lifespan. Helps you spot a shortfall (or surplus) early.',
  },
  {
    route: '/scenario-planner',
    target: '[data-testid="scenario-planner-page"]',
    title: 'Scenario Planner',
    content: 'Create at least one scenario. Change contributions, return assumptions, or retirement age — then compare side-by-side to see how the decisions ripple.',
  },
  {
    route: '/timeline',
    target: '[data-testid="timeline-page"]',
    title: 'Journey Timeline',
    content: 'A visual timeline of important financial and retirement milestones — from your next paycheck through Social Security, RMDs, and beyond.',
  },
  {
    route: '/social-security',
    target: '[data-testid="social-security-page"]',
    title: 'Social Security Optimizer',
    content: 'Compare claiming at 62, 67, or 70 to see the lifetime-benefit trade-off. Not personal advice — a starting point for the conversation.',
  },
  {
    route: '/roth-conversion',
    target: '[data-testid="roth-conversion-page"]',
    title: 'Roth Conversion Analyzer',
    content: 'Model potential Roth conversion scenarios and their multi-year tax implications. This tool is educational — not individualized tax advice.',
  },
  {
    route: '/rmd-planning',
    target: '[data-testid="rmd-planning-page"]',
    title: 'RMD Planner',
    content: "See your projected Required Minimum Distributions from age 73 onward, plus the tax picture each year. That's the tour — head back to the Dashboard whenever you're ready.",
  },
];

// -- Fallback selectors: if the specific data-testid isn't present, use body
const withFallback = (steps) =>
  steps.map((s) => ({
    ...s,
    target: s.target,
    placement: 'center',
    disableBeacon: true,
    content: s.content,
    title: s.title,
  }));

const GuidedTour = ({ open, onClose }) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Route the user to the current step's page whenever the step advances
  useEffect(() => {
    if (!run) return;
    const step = TOUR_STEPS[stepIndex];
    if (step && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [run, stepIndex, navigate, location.pathname]);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
      setRun(true);
    } else {
      setRun(false);
    }
  }, [open]);

  const finish = useCallback(async (kind) => {
    setRun(false);
    onClose?.();
    try {
      await api.post('/beta-program/tour-status', {
        event: kind === 'skip' ? 'tour_skipped' : 'tour_completed',
      });
    } catch (_e) {}
    track(kind === 'skip' ? 'tour_skipped' : 'tour_completed', { source: 'guided_tour' });
  }, [onClose]);

  const handleCallback = (data) => {
    const { status, action, index, type } = data;
    if ([STATUS.FINISHED].includes(status)) {
      finish('complete');
      return;
    }
    if ([STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      finish('skip');
      return;
    }
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      if (action === ACTIONS.NEXT) setStepIndex(index + 1);
      else if (action === ACTIONS.PREV) setStepIndex(Math.max(0, index - 1));
    }
  };

  return (
    <Joyride
      steps={withFallback(TOUR_STEPS)}
      run={run}
      stepIndex={stepIndex}
      callback={handleCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlayClose
      spotlightPadding={8}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      styles={{
        options: {
          arrowColor: '#0A1128',
          backgroundColor: '#0A1128',
          textColor: '#FFFFFF',
          overlayColor: 'rgba(10, 17, 40, 0.72)',
          primaryColor: '#C5A059',
          zIndex: 400,
          width: 420,
        },
        tooltip: { borderRadius: 8, padding: 20 },
        tooltipTitle: {
          color: '#C5A059',
          fontFamily: 'Cabinet Grotesk, Calibri, sans-serif',
          fontSize: 18,
          fontWeight: 600,
          textAlign: 'left',
          marginBottom: 8,
        },
        tooltipContent: { textAlign: 'left', color: '#E5EAF3', fontSize: 14, lineHeight: 1.55 },
        buttonNext: {
          backgroundColor: '#C5A059', color: '#0A1128', fontSize: 13, fontWeight: 700,
          padding: '8px 16px', borderRadius: 6,
        },
        buttonBack: { color: '#C5A059', fontSize: 13, fontWeight: 500 },
        buttonSkip: { color: '#A0AABF', fontSize: 13, fontWeight: 500 },
        buttonClose: { color: '#C5A059' },
      }}
    />
  );
};

export default GuidedTour;
export { TOUR_STEPS };
